import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit3, FiCpu, FiBarChart2, FiArrowRight } = FiIcons;

const steps = [
  {
    title: "Enter Your Salary",
    description: "Input your monthly salary, age, and retirement timeline to get started.",
    icon: FiEdit3,
    color: "bg-blue-50 text-blue-600",
    delay: 0.1
  },
  {
    title: "Generate Projection",
    description: "We calculate your EPF contributions using standard Malaysian rates and estimated returns.",
    icon: FiCpu,
    color: "bg-brand-50 text-brand-600",
    delay: 0.2
  },
  {
    title: "Visualize Your Future",
    description: "See your projected savings growth with detailed charts and totals.",
    icon: FiBarChart2,
    color: "bg-emerald-50 text-emerald-600",
    delay: 0.3
  }
];

const HowItWorks = () => {
  const scrollToCalculator = () => {
    const element = document.getElementById('calculator');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="how-it-works" className="py-24 bg-white scroll-mt-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-extrabold text-slate-900 tracking-tight mb-4"
          >
            How It Works
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-600"
          >
            Plan your EPF savings in 3 simple steps
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: step.delay }}
              className="group p-8 rounded-[2rem] bg-slate-50 border border-slate-100 hover:bg-white hover:border-brand-200 hover:shadow-2xl hover:shadow-brand-600/5 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-6">
                <div className={`w-14 h-14 ${step.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={step.icon} className="w-7 h-7" />
                </div>
                <span className="text-4xl font-black text-slate-200 group-hover:text-brand-100 transition-colors">
                  0{index + 1}
                </span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">{step.title}</h3>
              <p className="text-slate-500 leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="text-center pt-8"
        >
          <div className="inline-flex flex-col items-center">
            <p className="text-slate-900 font-bold text-lg mb-6">
              Start planning your EPF future today
            </p>
            <button
              onClick={scrollToCalculator}
              className="group btn-primary px-10 py-4 text-lg flex items-center gap-3"
            >
              Try Calculator Now
              <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;